"use client";

import {
  Fragment,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import NextImage from "next/image";
import { useReducedMotion } from "motion/react";
import { Lightbox } from "@/components/Lightbox";
import { useLazyVideo } from "@/components/motion/useLazyVideo";
import { getImageDimensions } from "@/resources/image-dimensions";
import styles from "./ProjectRiver.module.scss";

interface ProjectRiverProps {
  images: string[];
  title: string;
  /** Rendered MDX chunks (intro + one per ## section), interleaved between media groups. */
  blocks: ReactNode[];
  headerVideo?: string;
  /** First frame of headerVideo, resolved server-side in ProjectDetail. */
  headerPoster?: string;
  /** Overrides the 16:9 header frame, e.g. "1920 / 804" for scope footage. */
  headerAspect?: string;
}

const PORTRAIT = 0.9;

interface MediaItem {
  src: string;
  index: number;
  aspect: number;
}

/**
 * The detail-page media river: full-width and 2-up media rows flowing down
 * the content column, with the article's sections interleaved between media
 * groups. Landscape images run full-width; consecutive portraits pair into
 * 2-up rows where each keeps its natural aspect. One lightbox spans all
 * images.
 */
export function ProjectRiver({
  images,
  title,
  blocks,
  headerVideo,
  headerPoster,
  headerAspect,
}: ProjectRiverProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const reduceMotion = useReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);

  // The poster (preloaded by ProjectDetail) is the page's LCP, so the mp4
  // attaches only after load; useLazyVideo carries the iOS muted-autoplay
  // contract. If playback is refused, poster + native controls stay usable.
  useLazyVideo(videoRef, headerVideo ?? "", {
    mode: "afterLoad",
    reduceMotion: reduceMotion ?? false,
  });

  const groups = useMemo(() => {
    const withDims: MediaItem[] = images.map((src, index) => ({
      src,
      index,
      aspect: (() => {
        const d = getImageDimensions(src);
        return d.width / d.height;
      })(),
    }));
    const rows: MediaItem[][] = [];
    let i = 0;
    while (i < withDims.length) {
      const current = withDims[i];
      const next = withDims[i + 1];
      if (current.aspect < PORTRAIT && next && next.aspect < PORTRAIT) {
        rows.push([current, next]);
        i += 2;
      } else {
        rows.push([current]);
        i += 1;
      }
    }
    // Distribute rows around the text blocks: one group before each block
    // plus a trailing group, front-loaded so the hero group is the fullest.
    const groupCount = blocks.length + 1;
    const base = Math.floor(rows.length / groupCount);
    const extra = rows.length % groupCount;
    const distributed: MediaItem[][][] = [];
    let cursor = 0;
    for (let g = 0; g < groupCount; g++) {
      const size = base + (g < extra ? 1 : 0);
      distributed.push(rows.slice(cursor, cursor + size));
      cursor += size;
    }
    return distributed;
  }, [images, blocks.length]);

  const renderRow = (row: MediaItem[]) => (
    <div key={row[0].src} className={styles.row}>
      {row.map(({ src, index, aspect }) => {
        const solo = row.length === 1 && aspect < PORTRAIT;
        return (
          <button
            key={src}
            type="button"
            className={solo ? `${styles.tile} ${styles.solo}` : styles.tile}
            style={{
              aspectRatio: aspect,
              flexGrow: solo ? undefined : aspect,
            }}
            onClick={() => setLightboxIndex(index)}
            aria-label={`Enlarge image ${index + 1} of ${images.length}`}
          >
            <NextImage
              src={src}
              alt={`${title} (${index + 1})`}
              fill
              sizes="(max-width: 991px) 100vw, 67vw"
              quality={90}
              priority={index === 0}
              fetchPriority={index === 0 ? "high" : undefined}
            />
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      <div className={styles.river}>
        {headerVideo && (
          <div
            className={styles.video}
            style={headerAspect ? { aspectRatio: headerAspect } : undefined}
          >
            {headerVideo.endsWith(".mp4") ? (
              <video
                ref={videoRef}
                poster={headerPoster}
                muted
                loop
                playsInline
                controls
                preload="none"
                aria-label={`${title} video`}
              />
            ) : (
              <iframe
                src={headerVideo}
                title={`${title} video`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        )}
        {groups[0].map(renderRow)}
        {blocks.map((block, b) => (
          <Fragment key={b}>
            <div className={`${styles.block} oi-prose`}>{block}</div>
            {groups[b + 1].map(renderRow)}
          </Fragment>
        ))}
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          initialIndex={lightboxIndex}
          title={title}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
